import { useRef, useEffect, useState } from "react"
import {
  FileStack, Upload, FileText, Download, Trash2, Eye,
  Building2, User, File, FileImage, Info,
} from "lucide-react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { format } from "date-fns"
import { Link } from "react-router-dom"

import { api } from "@/lib/api"
import { APP_NAME } from "@/lib/appConfig"
import { useAuthStore } from "@/stores/authStore"
import { PageHeader } from "@/components/shared/PageHeader"
import { EmptyState } from "@/components/shared/EmptyState"
import { ListSkeleton } from "@/components/shared/LoadingSkeleton"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

interface Document {
  id: string
  file_name: string | null
  file_url: string
  file_size_kb: number | null
  uploaded_at: string
  category: string | null
  entity_type: string
  entity_id: string
  entity_name: string | null
  notes: string | null
  expiry_date: string | null
}

function formatSize(kb?: number | null) {
  if (!kb) return ""
  if (kb < 1024) return `${kb} KB`
  return `${(kb / 1024).toFixed(1)} MB`
}

function getFileExt(name?: string | null) {
  return name?.split(".").pop()?.toLowerCase() ?? ""
}

function isImage(name?: string | null) {
  return ["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(getFileExt(name))
}

function isPdf(name?: string | null) {
  return getFileExt(name) === "pdf"
}

function FileIcon({ name, className }: { name?: string | null; className?: string }) {
  if (isImage(name)) return <FileImage className={className} />
  if (isPdf(name)) return <FileText className={className} />
  return <File className={className} />
}

function entityColor(type: string) {
  if (type === "property") return "bg-blue-50 text-blue-600 border-blue-200"
  if (type === "tenant") return "bg-violet-50 text-violet-600 border-violet-200"
  return "bg-muted text-muted-foreground border-border"
}

function entityIconColor(type: string) {
  if (type === "property") return "text-blue-500"
  if (type === "tenant") return "text-violet-500"
  return "text-muted-foreground"
}

function entityLink(type: string, id: string) {
  if (type === "property") return `/properties/${id}`
  if (type === "tenant") return `/tenants/${id}`
  return null
}

function EntityPopover({ type, id, name }: { type: string; id: string; name: string | null }) {
  if (type === "general") return null

  const link = entityLink(type, id)
  const label = type === "property" ? "Property" : "Tenant"
  const Icon = type === "property" ? Building2 : User

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="inline-flex items-center justify-center h-5 w-5 rounded-full hover:bg-muted transition-colors"
          onClick={(e) => e.stopPropagation()}
        >
          <Info className={`h-3 w-3 ${entityIconColor(type)}`} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-3" side="top" align="start">
        <div className="flex items-start gap-2.5">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${type === "property" ? "bg-blue-50" : "bg-violet-50"}`}>
            <Icon className={`h-4 w-4 ${entityIconColor(type)}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-0.5">{label}</p>
            <p className="text-sm font-semibold truncate">{name ?? `Unnamed ${label}`}</p>
            {link && (
              <Link
                to={link}
                className={`text-[11px] font-medium mt-1.5 inline-block ${type === "property" ? "text-blue-600" : "text-violet-600"}`}
              >
                View {label} →
              </Link>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

async function downloadFile(url: string, fileName: string) {
  try {
    const res = await fetch(url)
    const blob = await res.blob()
    const blobUrl = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = blobUrl
    a.download = fileName
    a.click()
    URL.revokeObjectURL(blobUrl)
  } catch {
    window.open(url, "_blank", "noreferrer")
  }
}

export default function DocumentsPage() {
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [viewDoc, setViewDoc] = useState<Document | null>(null)
  const [deleteDoc, setDeleteDoc] = useState<Document | null>(null)

  useEffect(() => {
    const prev = document.title
    document.title = `Documents | ${APP_NAME}`
    return () => { document.title = prev }
  }, [])

  const { data: documents, isLoading, isError, refetch } = useQuery<Document[]>({
    queryKey: ["documents"],
    queryFn: async () => {
      const { data } = await api.get("/api/documents")
      return data.data
    },
    staleTime: 0,
    refetchOnMount: "always",
  })

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append("file", file)
      formData.append("entity_type", "general")
      formData.append("entity_id", user!.id)
      const { data } = await api.post("/api/documents/upload", formData)
      return data.data
    },
    onSuccess: () => {
      toast.success("Document uploaded")
      queryClient.invalidateQueries({ queryKey: ["documents"] })
      queryClient.invalidateQueries({ queryKey: ["plan", "usage"] })
    },
    onError: () => toast.error("Failed to upload document"),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/documents/${id}`)
    },
    onSuccess: () => {
      toast.success("Document deleted")
      queryClient.invalidateQueries({ queryKey: ["documents"] })
      queryClient.invalidateQueries({ queryKey: ["plan", "usage"] })
    },
    onError: () => toast.error("Failed to delete document"),
  })

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) uploadMutation.mutate(file)
    e.target.value = ""
  }

  return (
    <div className="flex flex-col h-full">
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleFileChange}
        accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp"
      />

      <PageHeader
        title="Documents"
        action={
          <Button
            size="sm"
            className="h-9 rounded-xl gap-1"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadMutation.isPending}
          >
            <Upload className="h-4 w-4" />
            {uploadMutation.isPending ? "Uploading…" : "Upload"}
          </Button>
        }
      />

      <div className="flex-1 overflow-y-auto pb-nav">
        {isLoading && <ListSkeleton count={6} />}

        {isError && (
          <div className="flex flex-col items-center py-16 px-8 text-center">
            <p className="text-sm text-muted-foreground mb-4">Failed to load documents.</p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>Try again</Button>
          </div>
        )}

        {!isLoading && !isError && documents?.length === 0 && (
          <EmptyState
            icon={<FileStack className="h-9 w-9 text-muted-foreground" />}
            title="No documents yet"
            description="Upload lease agreements, inspection reports, and other files to keep them organized."
          />
        )}

        {!isLoading && !isError && documents && documents.length > 0 && (
          <div className="px-4 pt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
            {documents.map((doc) => {
              const ext = getFileExt(doc.file_name).toUpperCase()
              // Records restored from the pre-migration backup have no file
              // behind them (empty file_url). Keep the record and its metadata,
              // but offer no view/save action that would resolve to nothing.
              const hasFile = !!doc.file_url
              const canPreview = hasFile && (isImage(doc.file_name) || isPdf(doc.file_name))
              const isGeneral = doc.entity_type === "general"

              return (
                <Card key={doc.id} className="rounded-2xl border-border overflow-hidden flex flex-col">
                  {/* Icon area */}
                  <div className="relative flex items-center justify-center bg-muted/40 py-6">
                    <div className="w-12 h-12 rounded-2xl bg-background shadow-sm flex items-center justify-center border border-border/50">
                      <FileIcon name={doc.file_name} className="h-6 w-6 text-primary" />
                    </div>
                    {ext && (
                      <span className="absolute top-2 right-2 text-[9px] font-bold font-mono bg-background border border-border/60 text-muted-foreground rounded px-1.5 py-0.5 leading-none">
                        {ext}
                      </span>
                    )}
                    {doc.expiry_date && (
                      <span className="absolute bottom-2 left-2 text-[9px] font-medium bg-amber-50 border border-amber-200 text-amber-700 rounded px-1.5 py-0.5 leading-none">
                        Expires {format(new Date(doc.expiry_date), "d MMM yy")}
                      </span>
                    )}
                  </div>

                  <CardContent className="p-3 flex flex-col flex-1 gap-2">
                    {/* Filename */}
                    <p className="text-sm font-semibold line-clamp-2 leading-snug min-h-[2.5rem]">
                      {doc.file_name ?? "Unnamed file"}
                    </p>

                    {/* Entity row */}
                    <div className="flex items-center gap-1 flex-wrap">
                      {!isGeneral ? (
                        <>
                          <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border ${entityColor(doc.entity_type)}`}>
                            {doc.entity_type === "property"
                              ? <Building2 className="h-2.5 w-2.5" />
                              : <User className="h-2.5 w-2.5" />
                            }
                            <span className="max-w-[80px] truncate">{doc.entity_name ?? doc.entity_type}</span>
                          </span>
                          <EntityPopover
                            type={doc.entity_type}
                            id={doc.entity_id}
                            name={doc.entity_name}
                          />
                        </>
                      ) : (
                        <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded border bg-muted text-muted-foreground border-border">
                          General
                        </span>
                      )}
                      {doc.category && (
                        <Badge variant="secondary" className="text-[9px] px-1.5 py-0 h-4 capitalize">
                          {doc.category}
                        </Badge>
                      )}
                    </div>

                    {/* Meta */}
                    <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground flex-wrap">
                      {doc.file_size_kb ? <span>{formatSize(doc.file_size_kb)}</span> : null}
                      {doc.file_size_kb && <span>·</span>}
                      <span>{format(new Date(doc.uploaded_at), "d MMM yyyy")}</span>
                    </div>

                    {doc.notes && (
                      <p className="text-[10px] text-muted-foreground italic line-clamp-1">{doc.notes}</p>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-1 mt-auto pt-1 border-t border-border/50">
                      {canPreview && (
                        <button
                          className="flex-1 flex items-center justify-center gap-1 text-[11px] font-medium text-foreground hover:text-primary hover:bg-primary/5 rounded-lg py-1.5 transition-colors"
                          onClick={() => setViewDoc(doc)}
                        >
                          <Eye className="h-3.5 w-3.5" />
                          View
                        </button>
                      )}
                      {hasFile ? (
                        <button
                          className="flex-1 flex items-center justify-center gap-1 text-[11px] font-medium text-foreground hover:text-primary hover:bg-primary/5 rounded-lg py-1.5 transition-colors"
                          onClick={() => downloadFile(doc.file_url, doc.file_name ?? "document")}
                        >
                          <Download className="h-3.5 w-3.5" />
                          Save
                        </button>
                      ) : (
                        <span className="flex-1 text-center text-[11px] text-muted-foreground py-1.5">
                          File unavailable
                        </span>
                      )}
                      <button
                        className="flex items-center justify-center h-7 w-7 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                        onClick={() => setDeleteDoc(doc)}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteDoc} onOpenChange={(open) => { if (!open) setDeleteDoc(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete document?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium text-foreground">{deleteDoc?.file_name ?? "This document"}</span> will be permanently deleted and cannot be recovered.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deleteDoc) deleteMutation.mutate(deleteDoc.id)
                setDeleteDoc(null)
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* In-app document viewer */}
      <Dialog open={!!viewDoc} onOpenChange={(open) => { if (!open) setViewDoc(null) }}>
        <DialogContent className="max-w-[95vw] w-[95vw] h-[95vh] p-0 flex flex-col">
          <DialogHeader className="px-5 py-3 border-b border-border flex-row items-center justify-between shrink-0">
            <DialogTitle className="text-sm font-semibold truncate flex items-center gap-2 min-w-0 pr-4">
              <FileIcon name={viewDoc?.file_name} className="h-4 w-4 text-primary shrink-0" />
              {viewDoc?.file_name ?? "Document"}
            </DialogTitle>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 rounded-lg gap-1.5 text-xs shrink-0 mr-8"
              onClick={() => viewDoc && downloadFile(viewDoc.file_url, viewDoc.file_name ?? "document")}
            >
              <Download className="h-3.5 w-3.5" />
              Download
            </Button>
          </DialogHeader>

          <div className="flex-1 min-h-0 bg-muted/30">
            {viewDoc && isImage(viewDoc.file_name) && (
              <div className="flex items-center justify-center p-6 h-full">
                <img
                  src={viewDoc.file_url}
                  alt={viewDoc.file_name ?? "document"}
                  className="max-w-full max-h-full rounded-xl shadow-md object-contain"
                />
              </div>
            )}
            {viewDoc && isPdf(viewDoc.file_name) && (
              <iframe
                src={viewDoc.file_url}
                title={viewDoc.file_name ?? "PDF"}
                className="w-full h-full"
                style={{ border: "none", display: "block" }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
